import { useCallback, useState } from 'react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import TrashIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Switch } from '@/components/ui/switch.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { useDeleteRestrictedWord } from '@/modules/restricted-words/hooks/use-delete-restricted-word.ts'
import { useEditRestrictedWord } from '@/modules/restricted-words/hooks/use-edit-restricted-word.ts'
import { useOverrideRestrictedWord } from '@/modules/restricted-words/hooks/use-override-restricted-word.ts'
import type { RestrictedWord } from '@/modules/restricted-words/types.ts'
import { DeleteWordsConfirmationDialog } from '@/modules/restricted-words/ui/delete-words-confirmation-dialog.tsx'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  open: boolean
  onClose: () => void
  word: RestrictedWord
}

export const EditRestrictedWordDialog = ({ open, onClose, word }: Props) => {
  const toast = useToast()
  const [text, setText] = useState(word.word)
  const [blockAllCombinations, setBlockAllCombinations] = useState(
    word.blockAllCombination,
  )
  const [disabled, setDisabled] = useState(word.disabled)
  const [warningOpen, setWarningOpen] = useState(false)
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const isCommon = word.common

  const { editRestrictedWord, isEditRestrictedWordLoading } =
    useEditRestrictedWord()
  const { overrideRestrictedWord, isOverrideRestrictedWordLoading } =
    useOverrideRestrictedWord()
  const { deleteRestrictedWord, isDeleteRestrictedWordLoading } =
    useDeleteRestrictedWord()

  const handleDelete = useCallback(() => {
    deleteRestrictedWord(word.id)
      .then(() => {
        toast.success('Successfully deleted blacklisted word')
        onClose()
      })
      .catch((error) => {
        handleError(
          error,
          'Failed to delete blacklisted word, please try again',
        )
      })
  }, [deleteRestrictedWord, toast, word.id, onClose])

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmationOpen(true)
  }, [])

  const handleDeleteClose = useCallback(
    () => setDeleteConfirmationOpen(false),
    [],
  )

  const handleDeleteConfirm = useCallback(() => {
    handleDeleteClose()
    handleDelete()
  }, [handleDelete, handleDeleteClose])

  const handleSaveClick = useCallback(() => {
    const updater = !isCommon
      ? editRestrictedWord({
          word: text,
          id: word.id,
          blockAllCombination: blockAllCombinations,
        })
      : overrideRestrictedWord({
          wordId: word.id,
          disabled: disabled,
          ...(disabled
            ? {}
            : { overriddenBlockAllCombination: blockAllCombinations }),
        })

    updater
      .then(() => {
        toast.success('Blacklisted word has been updated successfully')
        onClose()
      })
      .catch((error) => {
        handleError(
          error,
          'Failed to updated blacklisted word, please try again later',
        )
      })
  }, [
    blockAllCombinations,
    disabled,
    editRestrictedWord,
    isCommon,
    onClose,
    overrideRestrictedWord,
    text,
    toast,
    word.id,
  ])

  const isEditLoading =
    isEditRestrictedWordLoading || isOverrideRestrictedWordLoading
  const isUsedWrong = !isCommon && text.includes('*') && text.includes(' ')

  return (
    <>
      <Dialog show={open}>
        <Dialog.Header>
          <Dialog.Title>{`Edit${isCommon ? ' Default' : ''} Blacklisted Word`}</Dialog.Title>
        </Dialog.Header>

        <div className="mt-5 rounded-lg bg-secondary-50 p-4 dark:bg-secondary-600">
          <div className="mb-1 flex items-center">
            <p className="mr-1.5 text-sm font-medium">Blacklisted Word</p>
            <Tooltip
              open={warningOpen}
              side="right"
              contentClassName="max-w-[400px] text-start"
              content={
                <p className="text-xs font-medium">
                  If you want to match words starting or ending with certain
                  letters you can use the * character
                </p>
              }
            >
              <InfoIcon
                onMouseLeave={() => setWarningOpen(false)}
                onMouseEnter={() => setWarningOpen(true)}
              />
            </Tooltip>
          </div>
          <Tooltip
            open={isUsedWrong}
            contentClassName="max-w-[400px] text-start"
            className="w-full"
            content={
              <p className="text-xs font-medium">
                This function only works with single-word inputs and does not
                support inputs with spaces.
              </p>
            }
          >
            <TextField
              placeholder="Enter your words"
              value={text}
              onChange={(event) => setText(event.target.value)}
              disabled={isCommon}
              readOnly={isCommon}
              className={classNames({
                'bg-secondary-50 dark:bg-secondary-600': isCommon,
                'border border-warning-dark focus-within:border-warning-dark':
                  isUsedWrong,
              })}
            />
          </Tooltip>
          <div className="mt-2 flex items-center">
            <Checkbox
              checked={blockAllCombinations}
              onCheckedChange={(v) => setBlockAllCombinations(v as boolean)}
            />
            <p className="ml-1.5 text-xs text-text-sub">
              Block all combinations (Use with caution)
            </p>
          </div>
        </div>

        {isCommon && (
          <div className="mt-6 flex items-center">
            <Switch
              checked={disabled}
              onCheckedChange={setDisabled}
            />
            <p className="ml-2 text-sm font-medium">Disable</p>
          </div>
        )}

        {!isCommon && blockAllCombinations && (
          <div className="mt-5 rounded-lg bg-secondary-50 p-3 text-xs dark:bg-secondary-600">
            This could cause false positives! Let&apos;s say you enable it on
            &#34;ko&#34;, this would block the word &#34;koala&#34;.
          </div>
        )}

        <Dialog.Footer className="gap-2">
          <Button
            disabled={isEditLoading || isDeleteRestrictedWordLoading}
            className="flex-1"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          {!isCommon && (
            <Button
              disabled={isEditLoading}
              onClick={handleDeleteClick}
              isLoading={isDeleteRestrictedWordLoading}
              className="flex-1"
              variant="destructive"
            >
              <TrashIcon className="mr-1.5" /> Delete Word
            </Button>
          )}
          <Button
            isLoading={isEditLoading}
            disabled={isDeleteRestrictedWordLoading}
            className="flex-1"
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog>

      <DeleteWordsConfirmationDialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteClose}
        onDelete={handleDeleteConfirm}
      />
    </>
  )
}
